import { router, useRouter } from 'expo-router'
import { useCallback, useEffect, type ReactNode } from 'react'
import { View } from 'react-native'

import type { TPrayMutationData } from '@/src/features/pray/api/prayApi.types'
import { prefetchRewardCitations } from '@/src/features/pray/services/useRewardCitationsQuery'
import type { TSourceParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { useCanDismiss } from '@/src/general/hooks/useCanDismiss'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { useRoutes } from '@/src/general/utils/routes'

import type { INTENTION_TYPE } from '../../models/Intention.enums'
import { useIntentionQuery } from '../../services/useIntentionQuery'
import { IntentionPray } from '../components/IntentionPray'

type TIntentionsPrayScreenProps = {
  intentionId: number
  shareToken?: string
  type: INTENTION_TYPE
  onClose: VoidFunction
  redirect: string
  theme?: string
  skipReward?: boolean
  skipPrefetch?: boolean
} & TSourceParam

export const IntentionPrayScreen = ({
  intentionId,
  onClose,
  shareToken,
  source,
  type,
  theme,
  redirect,
  skipReward = false,
  skipPrefetch = false,
}: TIntentionsPrayScreenProps): ReactNode => {
  const { routes } = useRoutes()
  const { dismiss } = useRouter()
  const { canDismissTo } = useCanDismiss()

  const { intention, error, isSuccess } = useIntentionQuery(intentionId, shareToken, type)

  useEffect(() => {
    if (skipPrefetch) return
    prefetchRewardCitations({ type: undefined })
  }, [skipPrefetch])

  const handleAmen = useCallback(
    (prayer?: TPrayMutationData) => {
      if (skipReward && canDismissTo(1)) {
        dismiss(1)
      } else if (skipReward) {
        router.navigate(redirect)
      } else {
        router.navigate(
          routes.intentionByIdReward({
            id: intentionId,
            shareToken,
            source,
            redirect,
            type,
            theme,
            prayerId: prayer?.id ?? null,
          }),
        )
      }
    },
    [routes, intentionId, shareToken, canDismissTo, dismiss, source, redirect, type, skipReward, theme],
  )

  if (error || (isSuccess && !intention))
    return (
      <ErrorScreen
        error={error}
        buttons={[{ onPress: onClose }]}
        defaultErrorKey="intention.load.error"
        testID={TEST_ID.PRAY.SCREEN.ERROR}
      />
    )

  return (
    <View testID={TEST_ID.INTENTIONS.SCREEN.PRAY_SCREEN} className="flex-1">
      <IntentionPray
        intention={intention}
        onAmen={handleAmen}
        onClose={onClose}
        theme={theme}
        type={type}
        trackingSource={source}
      />
    </View>
  )
}
